import { SelectionModel } from '@angular/cdk/collections';
import { CdkMenuModule } from '@angular/cdk/menu';
import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { DestroyRef, type OnInit, type SimpleChanges, type TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { toggleScroll } from '../../shared/others/toggle-scroll/index';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirCheckboxComponent } from '../checkbox/checkbox.component';
import { PalantirContentComponent } from '../content/content.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirProgressComponent } from '../progress-bar/progress-bar.component';
import { PalantirStarsComponent } from '../stars/stars.component';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import { PalantirTableAvatarComponent } from './components/columns/avatar/avatar.component';
import { PalantirTableAvatarGroupComponent } from './components/columns/avatar-group/avatar-group.component';
import { PalantirTableDropdownComponent } from './components/columns/dropdown/dropdown.component';
import { PalantirTableLoadingComponent } from './components/columns/loading/loading.component';
import { PalantirStatusComponent } from './components/columns/status/status.component';
import { PalantirTableTagsComponent } from './components/columns/tags/tags.component';
import { PalantirTableSkeletonComponent } from './components/skeleton/skeleton.component';
import type {
  PalantirTableData,
  PalantirTableDataSource,
  PalantirTableSortEvent,
  PalantirTableTextItem,
} from './table.type';

@Component({
  selector: 'palantir-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    CdkTableModule,
    CdkMenuModule,
    PalantirIconComponent,
    PalantirTextComponent,
    PalantirTableSkeletonComponent,
    PalantirCheckboxComponent,
    PalantirContentComponent,
    PalantirStatusComponent,
    PalantirStarsComponent,
    PalantirButtonComponent,
    PalantirTableTagsComponent,
    PalantirTableAvatarComponent,
    PalantirProgressComponent,
    PalantirTableAvatarGroupComponent,
    PalantirTableDropdownComponent,
    PalantirTableLoadingComponent,
    PalantirTooltipModule,
  ],
})
export class PalantirTableComponent implements OnInit {
  @Output() public readonly sort = new EventEmitter<PalantirTableSortEvent>();

  @Output() public readonly tableSelectChange = new EventEmitter<PalantirTableDataSource[]>();

  @Input({ required: true }) public data!: PalantirTableData;

  @Input() public isLoading: boolean = false;

  @Input() public isSearching: boolean = false;

  @Input() public selectedItems: PalantirTableDataSource[] = [];

  @Input() public customTemplates: { [key: string]: TemplateRef<any> } = {};

  public selection = new SelectionModel<PalantirTableDataSource>(true, []);

  public displayedColumns: string[] = [];

  public dataSource: PalantirTableDataSource[] = [];

  public selectedSort = signal<PalantirTableSortEvent>({
    active: '',
    direction: 'desc',
  });

  public blockScroll$: Subject<boolean> = new Subject<boolean>();

  public constructor(private readonly destroyRef: DestroyRef) {}

  public ngOnInit() {
    this.selection.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selected) => {
      this.tableSelectChange.emit(selected.source.selected);
    });

    this.dataSource = this.data.dataSource;
    this.displayedColumns = this.data.displayedColumns.map((column) => column.key);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { disableScroll, enableScroll } = toggleScroll();

    this.blockScroll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => (value ? disableScroll() : enableScroll()));
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!changes['selectedItems']?.currentValue.length) {
      this.selection.clear();
    }

    if (
      JSON.stringify(changes['selectedItems']?.currentValue) !==
      JSON.stringify(changes['selectedItems']?.previousValue)
    ) {
      this.selection.clear();
      this.selection.select(...this.selectedItems);
    }

    if (changes['data']?.currentValue) {
      this.displayedColumns = this.data.displayedColumns.map((column) => column.key);
    }
  }

  public isAllSelected() {
    const selectedIds = this.selection.selected.map(
      (item) => (item['id'] as PalantirTableTextItem).value,
    );

    return this.data.dataSource.every((item) =>
      selectedIds.includes((item['id'] as PalantirTableTextItem).value),
    );
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.data.dataSource);
  }

  public trackByFn(index: number): number {
    return index;
  }

  public sortData(column: string): void {
    const isSameColumn = this.selectedSort().active === column;

    const currentDirection = this.selectedSort().direction;

    if (isSameColumn && currentDirection === 'desc') {
      this.selectedSort.set({ active: '' });
      this.sort.emit({ active: '' });

      return;
    }

    const direction = this.getSortDirection(isSameColumn, currentDirection);

    this.selectedSort.set({ active: column, direction });

    this.sort.emit({ active: column, direction });
  }

  private getSortDirection(
    isSameColumn: boolean,
    currentDirection?: 'asc' | 'desc',
  ): 'asc' | 'desc' {
    if (!isSameColumn) {
      return 'asc';
    }

    return currentDirection === 'asc' ? 'desc' : 'asc';
  }
}
